// Shared chrome for the Control Plane section.
//
// Two rules run through everything here.
//
// 1. NO STATUS IS CARRIED BY COLOUR ALONE. Every chip states a word. Colour is
//    the second signal and never the only one — an operator who is colour-blind,
//    on a washed-out monitor, or simply moving fast still reads the state.
//
// 2. DEV AND PRODUCT MUST BE IMPOSSIBLE TO CONFUSE. `LaneChip` prints the lane
//    name, and PRODUCT additionally gets a heavier border and a filled ground.
//    Publishing to PRODUCT carries a typed confirmation on top of that.
//
// Colours come only from tokens, never as literals, which is what lets this
// section carry the light palette (`[data-cp-theme="light"]`) that the rest of
// the app cannot yet.

import type { CSSProperties, ReactNode } from "react";
import type { Lane } from "./contract";
import { card, sectionLabel, TONE_COLOR, type StatusTone } from "./styles";

export function Card(props: {
  children: ReactNode;
  title?: ReactNode;
  note?: ReactNode;
  accent?: "ok" | "warn" | "danger" | "accent";
  style?: CSSProperties;
}) {
  const border =
    props.accent === undefined ? "var(--border)" : `var(--${props.accent})`;
  return (
    <section style={{ ...card, borderColor: border, ...props.style }}>
      {props.title !== undefined && (
        <header
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              ...sectionLabel,
              color: props.accent ? `var(--${props.accent})` : "var(--fg)",
            }}
          >
            {props.title}
          </span>
          {props.note !== undefined && (
            <span style={{ color: "var(--fg-dim)" }}>{props.note}</span>
          )}
        </header>
      )}
      <div style={{ padding: "14px 16px" }}>{props.children}</div>
    </section>
  );
}

/** The lane, stated. Never a bare colour — and never guessed.
 *
 *  `lane` is nullable on purpose. Until the server has said which lane it is
 *  serving, this renders UNKNOWN rather than defaulting to DEV. A console whose
 *  whole thesis is that lanes must be unconfusable cannot assert the safest-
 *  sounding lane when its request failed; an operator reading "DEV" on a page
 *  that never reached the server is worse off than one reading nothing. */
export function LaneChip({ lane, host }: { lane: Lane | null; host?: string }) {
  const isProduct = lane === "production";
  const label =
    lane === null
      ? "UNKNOWN"
      : lane === "production"
        ? "PRODUCT"
        : lane === "test"
          ? "TEST"
          : "DEV";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: 4,
        border: `${isProduct ? 2 : 1}px solid ${isProduct ? "var(--danger)" : "var(--border)"}`,
        background: isProduct ? "var(--danger-bg, #2a1416)" : "var(--bg-elev)",
      }}
    >
      <span style={sectionLabel}>Lane</span>
      <b
        style={{
          letterSpacing: 0.4,
          color: isProduct ? "var(--danger)" : "var(--fg)",
        }}
      >
        {label}
      </b>
      {host !== undefined && (
        <span style={{ color: "var(--fg-dim)" }}>{host}</span>
      )}
    </span>
  );
}

/** A status is always a WORD in a bordered chip. */
export function StatusChip({
  tone,
  children,
}: {
  tone: StatusTone;
  children: ReactNode;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "1px 8px",
        border: `1px solid ${TONE_COLOR[tone]}`,
        borderRadius: 3,
        color: TONE_COLOR[tone],
        fontSize: 11,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** Diff gutter marks, drawn rather than typed, so they keep their meaning at
 *  any zoom and never depend on a font shipping a glyph. */
export function DiffGlyph({ kind }: { kind: "added" | "removed" | "modified" | "reordered" }) {
  const color =
    kind === "added"
      ? "var(--ok)"
      : kind === "removed"
        ? "var(--danger)"
        : kind === "modified"
          ? "var(--accent)"
          : "var(--link)";
  const label =
    kind === "added"
      ? "added"
      : kind === "removed"
        ? "removed"
        : kind === "modified"
          ? "modified"
          : "reordered";
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      role="img"
      aria-label={label}
      style={{ flex: "0 0 auto" }}
    >
      {kind === "added" && (
        <>
          <path d="M7 3 V11" stroke={color} strokeWidth="1.6" />
          <path d="M3 7 H11" stroke={color} strokeWidth="1.6" />
        </>
      )}
      {kind === "removed" && <path d="M3 7 H11" stroke={color} strokeWidth="1.6" />}
      {kind === "modified" && (
        <path
          d="M2.5 8 q2.2 -3 4.5 0 t4.5 0"
          fill="none"
          stroke={color}
          strokeWidth="1.5"
        />
      )}
      {kind === "reordered" && (
        <>
          <path d="M5 9.5 V3.5 M3 5.5 L5 3.5 L7 5.5" fill="none" stroke={color} strokeWidth="1.4" />
          <path d="M9 4.5 V10.5 M7 8.5 L9 10.5 L11 8.5" fill="none" stroke={color} strokeWidth="1.4" />
        </>
      )}
    </svg>
  );
}

export function Btn(props: {
  children: ReactNode;
  onClick?: () => void;
  tone?: "default" | "primary" | "danger";
  disabled?: boolean;
  title?: string;
  type?: "button" | "submit";
}) {
  const tone = props.tone ?? "default";
  return (
    <button
      type={props.type ?? "button"}
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      style={{
        borderColor:
          tone === "primary"
            ? "var(--accent)"
            : tone === "danger"
              ? "var(--danger)"
              : "var(--border)",
        color:
          tone === "primary"
            ? "var(--accent)"
            : tone === "danger"
              ? "var(--danger)"
              : "var(--fg)",
        background: tone === "danger" ? "var(--danger-bg, #2a1416)" : "var(--bg-elev)",
      }}
    >
      {props.children}
    </button>
  );
}

export function Kv({ k, children }: { k: string; children: ReactNode }) {
  return (
    <>
      <dt style={{ color: "var(--fg-dim)", margin: 0 }}>{k}</dt>
      <dd style={{ margin: 0, wordBreak: "break-word" }}>{children}</dd>
    </>
  );
}

export function KvList({ children }: { children: ReactNode }) {
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(96px, max-content) 1fr",
        gap: "5px 12px",
        margin: 0,
      }}
    >
      {children}
    </dl>
  );
}

/** A short hash is unreadable in full and ambiguous when truncated to four
 *  characters. Eight and eight, with the whole value in the title. */
export function Hash({ value }: { value: string | null }) {
  if (!value) return <span style={{ color: "var(--fg-dim)" }}>—</span>;
  const short =
    value.length > 20 ? `${value.slice(0, 8)}…${value.slice(-8)}` : value;
  return (
    <span title={value} style={{ color: "var(--fg-dim)" }}>
      {short}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div style={{ color: "var(--fg-dim)", padding: "18px 0" }}>{children}</div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        ...card,
        borderColor: "var(--danger)",
        background: "var(--danger-bg, #2a1416)",
        color: "var(--danger)",
        padding: "10px 14px",
      }}
    >
      {children}
    </div>
  );
}

/** Section header with an actions slot.
 *
 * Deliberately local rather than an extra prop on the shared `PageHeader`:
 * that component is used by 21 other pages, and widening a shared signature to
 * suit one section is how a shared component stops being shared. */
export function CpHeader(props: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        flexWrap: "wrap",
        marginBottom: 4,
      }}
    >
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        {props.breadcrumb !== undefined && (
          <div style={{ color: "var(--fg-dim)", marginBottom: 2 }}>
            {props.breadcrumb}
          </div>
        )}
        <h2 style={{ margin: 0, fontSize: 16 }}>{props.title}</h2>
        {props.subtitle !== undefined && (
          <p style={{ margin: "4px 0 0", color: "var(--fg-dim)" }}>
            {props.subtitle}
          </p>
        )}
      </div>
      {props.actions !== undefined && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {props.actions}
        </div>
      )}
    </header>
  );
}
